/**
 * Lohri drawings — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_drawing_topics`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lohriDrawingsPack: ToilePack = {
    slug: 'lohri-drawings',
    category: 'coloring',
    name: 'Lohri drawings',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fire', label: 'Lohri bonfire', subject: 'a bonfire with people sitting around it, popcorn and rewri in a bowl'},
        {key: 'dance', label: 'Bhangra dancers', subject: 'a man and woman dancing bhangra and giddha beside a dhol'},
        {key: 'harvest', label: 'Harvest field', subject: 'sugarcane and wheat in a field with kites in the sky'},
    ],
    styles: [
        {key: 'pencil', label: 'Pencil sketch', style: 'a soft graphite pencil sketch with clear outlines and light hatching for shade', palette: 'Gray pencil on white paper, no color.', framing: 'Drawn on a plain white paper sheet, centered with a white margin.'},
        {key: 'crayon', label: 'Colour pencil', style: 'a colour pencil and crayon drawing, simple outlines filled with bright even strokes of colour', palette: 'Bright crayon colours on white paper.', framing: 'Drawn on a plain white drawing-book page, centered with a white margin.'},
        {key: 'outline', label: 'Outline to colour', style: 'a bold black outline drawing with flat marker colour filling each shape', palette: 'Black outlines with flat bright colour fills on white paper.', framing: 'Flat 2D drawing isolated on a pure white background with a white margin, clean closed black line work.'},
    ],
    directions: ['a kite', 'a village', 'a dhol'],
    directionHint: 'try: a kite',
    seo: {
        title: 'Lohri Drawings: Bonfire, Bhangra Dancers, Harvest Field',
        metaDescription: 'Nine Lohri drawings: a bonfire with popcorn and rewri, bhangra dancers and a harvest field, in pencil, colour and outline.',
        h1: 'Lohri drawings: bonfire, bhangra dancers, harvest field',
        tagline: 'Three scenes, each in pencil sketch, colour pencil and outline to colour.',
        intro: 'Lohri drawings for a competition, a festival chart or a class project. The set shows people sitting around a bonfire with popcorn and rewri in a bowl, a man and a woman dancing bhangra and giddha beside a dhol, and sugarcane and wheat fields with kites overhead. Each comes in three styles: pencil, colour pencil and outline to colour. Type a change in the direction field, such as a kite, a village or a dhol.',
        useCases: [
            'a Lohri poster for a school',
            'a festival chart for a class',
            'a colouring page for a competition',
            'a classroom display on Indian festivals',
        ],
        faq: [
            {q: 'Can I print these for a school competition?', a: 'Yes. Each picture is a plain drawing on a white background, so it prints on ordinary paper.'},
            {q: 'What is the difference between the three styles?', a: 'Pencil sketch is grey shading with no colour. Colour pencil is bright crayon colour. Outline to colour is a bold black line drawing with flat colour inside each shape, easy to fill in.'},
            {q: 'Who is dancing?', a: 'A man and a woman dance bhangra and giddha, the folk dances of Punjab, beside a dhol drum.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type the change in the direction field. It is a request, so the result may differ from the nine shown.'},
        ],
    },
}
